import { useMemo, useState } from "react";
import { ActionIcon, Badge, Group, Stack, Text, Tooltip } from "@mantine/core";
import { Alert, Button, ConfirmModal, PageHeader, SectionCard, spacePx } from "@xplatform/design-system";
import { IconEye, IconPencil, IconPlus, IconShieldLock, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { Role, RoleModule } from "@models/access";
import { accessApi } from "@services/accessApi";
import { AdminTable, col, COLUMN_WIDTH } from "@components/AdminTable";
import { errorMessage, useApiResource } from "@hooks/monitoring/useApiResource";
import { ACCESS_KEYS, useEscalationGuard, useRoles } from "@hooks/access";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { EnvironmentBanner, RoleEditorDrawer } from "@features/Access";

/** Every permission of the application, none assigned (what a new role starts from). */
const blankModules = (modules: RoleModule[]): RoleModule[] =>
  modules.map((m) => ({ ...m, permissions: m.permissions.map((p) => ({ ...p, assigned: false })) }));

/**
 * The roles of this application. Roles are application-wide in BeeHive, so an
 * edit reaches every user holding the role in every organisation; the table and
 * the editor show that global count beside the DSML one.
 */
export const RolesPage = () => {
  const { t } = useTranslation();
  const { canManageAccess } = useAdminPermissions();
  const { canGrantRole, canEditRole } = useEscalationGuard();
  const roles = useRoles();
  const [editing, setEditing] = useState<Role | "new" | null>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // A new role needs the application's permission list; any role carries it.
  // Only without any role does the page ask the catalogue for it.
  const needCatalogue = roles.data !== null && roles.data.length === 0 && editing === "new";
  const catalogue = useApiResource(needCatalogue ? ACCESS_KEYS.permissions : null,
                                   (signal) => accessApi().permissions(signal));
  const modules = useMemo<RoleModule[]>(() => {
    const first = roles.data?.[0];
    if (first) return blankModules(first.modules);
    return (catalogue.data?.modules ?? []).map((m) => ({
      name: m.name, permissions: m.permissions.map((p) => ({ key: p.key, name: p.name, assigned: false })),
    }));
  }, [roles.data, catalogue.data]);

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await accessApi().deleteRole(deleting.id);
      setDeleting(null);
      roles.reload();
    } catch (err) {
      setDeleteError(errorMessage(err));
    } finally {
      setDeleteBusy(false);
    }
  };

  const deleteBlocked = (role: Role): string | null =>
    role.assignedUsers > 0 ? t("admin.access.roles.deleteInUse", { count: role.assignedUsers })
      : role.heldByActor ? t("admin.access.roles.editor.heldByYou")
      : !canGrantRole(role) ? t("admin.access.roles.editor.tooStrong")
      : null;

  const columns = [
    col.twoLine<Role>("name", t("admin.access.roles.columns.name"), (r) => ({ primary: r.name, secondary: r.description })),
    col.badge<Role>("scopes", t("admin.access.roles.columns.accessScopes"), (r) => (
      <Group gap={spacePx[1]} wrap="nowrap">
        {r.accessScopes.map((s) => (
          <Tooltip key={s} label={t(`admin.access.roles.scopes.${s}`)}>
            <Badge size="xs" variant="light" color="gray">{s.split("_").map((w) => w[0]).join("")}</Badge>
          </Tooltip>
        ))}
      </Group>
    ), 120),
    col.badge<Role>("users", t("admin.access.roles.columns.assigned"), (r) => (
      <Text size="sm">
        {t("admin.access.roles.assigned", { count: r.assignedUsers, inScope: r.assignedUsersInScope })}
      </Text>
    ), 200),
    col.date<Role>("updated", t("admin.access.roles.columns.updated"), (r) => r.updatedAt),
    {
      key: "actions",
      header: t("admin.table.columns.actions"),
      width: COLUMN_WIDTH.actions,
      align: "right" as const,
      render: (role: Role) => {
        const editable = canManageAccess && canEditRole(role);
        const blocked = deleteBlocked(role);
        return (
          <Group gap="xs" justify="flex-end" wrap="nowrap">
            <Tooltip label={t(editable ? "admin.access.roles.edit" : "admin.access.roles.view")}>
              <ActionIcon variant="subtle" aria-label={t(editable ? "admin.access.roles.edit" : "admin.access.roles.view")}
                          onClick={(e) => { e.stopPropagation(); setEditing(role); }}>
                {editable ? <IconPencil size={16} /> : <IconEye size={16} />}
              </ActionIcon>
            </Tooltip>
            {canManageAccess && (
              <Tooltip label={blocked ?? t("admin.access.roles.delete")}>
                <ActionIcon variant="subtle" color="red" aria-label={t("admin.access.roles.delete")}
                            disabled={blocked !== null}
                            onClick={(e) => { e.stopPropagation(); setDeleteError(null); setDeleting(role); }}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        );
      },
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title={t("admin.access.roles.title")}
        description={t("admin.access.roles.description")}
        icon={<IconShieldLock size={28} />}
        actions={canManageAccess && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing("new")} disabled={!roles.data}>
            {t("admin.access.roles.create")}
          </Button>
        )}
      />
      <EnvironmentBanner />

      {roles.error && (
        <Alert color={roles.forbidden ? "gray" : "red"} title={t("admin.access.roles.loadFailed")}>{roles.error}</Alert>
      )}
      <SectionCard>
        <AdminTable
          columns={columns}
          data={roles.data ?? []}
          getRowKey={(r) => r.id}
          onRowClick={(r) => setEditing(r)}
          loading={roles.loading}
          emptyMessage={t("admin.access.roles.empty")}
        />
      </SectionCard>

      <RoleEditorDrawer
        opened={editing !== null}
        role={editing === "new" ? null : editing}
        modules={editing && editing !== "new" ? editing.modules : modules}
        canManage={canManageAccess}
        onClose={() => setEditing(null)}
        onSaved={roles.reload}
      />

      <ConfirmModal
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t("admin.access.roles.deleteTitle", { role: deleting?.name ?? "" })}
        confirmLabel={t("admin.access.roles.delete")}
        cancelLabel={t("common.cancel")}
        loading={deleteBusy}
        portalProps={{ target: "#admin-portal-root" }}
        danger
      >
        <Stack gap="sm">
          <Text size="sm">{t("admin.access.roles.deleteMessage")}</Text>
          {deleteError && <Alert color="red">{deleteError}</Alert>}
        </Stack>
      </ConfirmModal>
    </Stack>
  );
};
